import { Box, Typography } from "@mui/material";
import { alpha, keyframes, type Theme } from "@mui/material/styles";

/**
 * "What is the agent doing right now" — the focal card on Today.
 *
 * Compact, glance-in-two-seconds composition. A single tone dot + the
 * status sentence sit on one horizontal row at the top, with the
 * "right now / mission chip" cluster pushed to the right; the
 * reflection quote floats to the bottom via `mt-auto` so the card still
 * equalises with the neighbouring `MissionBanner` when they share a row
 * on `lg+`.
 *
 *   ┌──────────────────────────────────────────────────────────────┐
 *   │ ⦿ Agent is healthy — between missions.   RIGHT NOW [▢ chip]  │
 *   │                                                              │
 *   │ │ "the previous searches yielded no relevant results…"       │
 *   └──────────────────────────────────────────────────────────────┘
 *
 * ONE status, not two. The status is shown exactly once — the dot
 * carries the tone (and an accessible "Live / Paused / Watch / Down"
 * label), the sentence carries the detail. We deliberately dropped the
 * old "AGENT · LIVE" eyebrow that duplicated the "Agent is healthy"
 * sentence. Operator pause is the top-level truth: when the agent is
 * paused the dot is steady amber and the sentence reads "Agent paused…"
 * — never a hardcoded "Live / healthy".
 *
 * Container queries drive internal type sizing so the same card stays
 * balanced at full width on mobile and at the narrower 60% column on
 * desktop. On viewports too narrow to fit both chunks the row wraps
 * gracefully via `flexWrap`.
 */

// One-shot entrance (was the `.rise-in` utility in globals.css). Pure
// transform + opacity, exponential ease-out.
const riseIn = keyframes({
  from: { opacity: 0, transform: "translateY(6px)" },
  to: { opacity: 1, transform: "translateY(0)" },
});

// "Agent is live" indicator (was the `.live-ping` utility). Outer ring
// expands once every ~2 s and fades; inner dot stays solid.
const livePing = keyframes({
  "0%": { transform: "scale(1)", opacity: 0.55 },
  "80%": { transform: "scale(2.4)", opacity: 0 },
  "100%": { transform: "scale(2.4)", opacity: 0 },
});

export function AgentHero({
  statusLine,
  isPaused = false,
  currentMissionLabel,
  lastReflection,
}: {
  statusLine: string;
  /** Operator pause overrides the derived tone: a paused agent is amber
   * "Paused", never green "Live", no matter what the status sentence
   * happens to say. */
  isPaused?: boolean;
  currentMissionLabel: string | null;
  lastReflection: string | null;
}): JSX.Element {
  const tone = deriveTone(statusLine, isPaused);
  const sleeping = /sleep|budget spent/i.test(statusLine);
  // The single short status word, carried on the dot as its accessible
  // name. Paused / Asleep are their own words so the operator never mistakes a
  // paused or budget-sleeping agent for a merely "watch" one.
  const toneWord = isPaused
    ? "Paused"
    : sleeping
      ? "Asleep"
      : tone === "good"
        ? "Live"
        : tone === "warn"
          ? "Watch"
          : "Down";
  return (
    <Box
      component="section"
      aria-label="Agent status"
      sx={{
        position: "relative",
        display: "flex",
        height: "100%",
        flexDirection: "column",
        gap: 1.5,
        overflow: "hidden",
        borderRadius: 1,
        border: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
        boxShadow: (t: Theme) => t.shadows[1],
        p: 2,
        containerType: "inline-size",
        animation: `${riseIn} 460ms cubic-bezier(0.16,1,0.3,1) both`,
        "@media (prefers-reduced-motion: reduce)": { animation: "none" },
        "@container (min-width:28rem)": { p: 2.5 },
      }}
    >
      {/* Decorative brand halo — barely-there marker that this is the
       * primary card on the page. Doesn't cost any layout space. */}
      <Box
        aria-hidden
        sx={{
          pointerEvents: "none",
          position: "absolute",
          right: -64,
          top: -64,
          height: 160,
          width: 160,
          borderRadius: "50%",
          bgcolor: (t: Theme) => alpha(t.palette.primary.main, 0.14),
          opacity: 0.7,
          filter: "blur(48px)",
        }}
      />

      <Box sx={{ position: "relative", display: "flex", flexWrap: "wrap", alignItems: "center", columnGap: 2, rowGap: 1 }}>
        <Box sx={{ display: "flex", minWidth: 0, flex: "1 1 auto", alignItems: "center", gap: 1.25 }}>
          <LiveDot tone={tone} label={`Agent status: ${toneWord}`} />
          <Typography
            component="p"
            sx={{
              minWidth: 0,
              textWrap: "balance",
              fontSize: "1.0625rem",
              fontWeight: 600,
              lineHeight: 1.25,
              letterSpacing: "-0.01em",
              color: "text.primary",
              "@container (min-width:28rem)": { fontSize: "1.25rem" },
            }}
          >
            {statusLine}.
          </Typography>
        </Box>

        {currentMissionLabel && (
          <Box sx={{ ml: "auto", display: "flex", flexShrink: 1, minWidth: 0, alignItems: "center", gap: 1 }}>
            <Box
              component="span"
              sx={{
                display: "none",
                fontSize: 10,
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.16em",
                color: "text.disabled",
                "@container (min-width:28rem)": { display: "inline" },
              }}
            >
              Right now
            </Box>
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                minWidth: 0,
                alignItems: "center",
                gap: 1,
                borderRadius: 999,
                bgcolor: (t: Theme) => alpha(t.palette.primary.main, 0.14),
                px: 1.5,
                py: 0.5,
                fontSize: "0.8125rem",
                fontWeight: 500,
                color: "primary.main",
              }}
            >
              <Box aria-hidden component="span" sx={{ height: 6, width: 6, flexShrink: 0, borderRadius: "50%", bgcolor: "primary.main" }} />
              <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {currentMissionLabel}
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      {lastReflection && (
        <Box
          component="blockquote"
          sx={{
            position: "relative",
            m: 0,
            borderLeft: 2,
            borderColor: "divider",
            pl: 1.5,
            fontSize: "0.8125rem",
            fontStyle: "italic",
            lineHeight: 1.6,
            color: "text.secondary",
          }}
        >
          {lastReflection}
        </Box>
      )}
    </Box>
  );
}

type Tone = "good" | "warn" | "bad";

function LiveDot({ tone, label }: { tone: Tone; label: string }): JSX.Element {
  const dotColor =
    tone === "good"
      ? "success.main"
      : tone === "warn"
        ? "warning.main"
        : "error.main";
  // Only the live (good) tone pulses — a paused / degraded agent shows a
  // steady dot, so a pulsing ring never implies activity that isn't
  // happening.
  const ping = tone === "good";
  return (
    <Box
      component="span"
      role="img"
      aria-label={label}
      sx={{ position: "relative", display: "inline-flex", flexShrink: 0, height: 8, width: 8 }}
    >
      {ping && (
        <Box
          aria-hidden
          component="span"
          sx={{
            position: "absolute",
            inset: 0,
            borderRadius: "50%",
            bgcolor: dotColor,
            animation: `${livePing} 2.2s cubic-bezier(0.16,1,0.3,1) infinite`,
            "@media (prefers-reduced-motion: reduce)": { animation: "none" },
          }}
        />
      )}
      <Box
        aria-hidden
        component="span"
        sx={{ position: "relative", display: "inline-flex", height: 8, width: 8, borderRadius: "50%", bgcolor: dotColor }}
      />
    </Box>
  );
}

function deriveTone(statusLine: string, isPaused: boolean): Tone {
  // Operator pause is the top-level truth — a paused agent is amber
  // regardless of what the status sentence says.
  if (isPaused) return "warn";
  const s = statusLine.toLowerCase();
  if (
    s.includes("paused") ||
    s.includes("sleep") ||
    s.includes("budget") ||
    s.includes("bootstrap") ||
    s.includes("watch")
  ) {
    return "warn";
  }
  if (s.includes("unreach") || s.includes("down") || s.includes("error")) {
    return "bad";
  }
  return "good";
}
